<script setup lang="ts">
/**
 * Preview dialog for one file or a set of files: the name and „2 von 5 ·
 * 1,5 MB“ in the head, a download button, and — with several files — back and
 * forward buttons plus the arrow keys to step through them. The body is
 * NxFilePreview, so every kind it knows shows here too.
 *
 * `files` is the list; `v-model:index` the file on screen; `v-model:open` the
 * dialog.
 */
import ChevronLeft from 'lucide-vue-next/dist/esm/icons/chevron-left.js'; import ChevronRight from 'lucide-vue-next/dist/esm/icons/chevron-right.js'; import Download from 'lucide-vue-next/dist/esm/icons/download.js';
import { computed, inject } from 'vue';
import { formatBytes } from '../Attachment/fileKind';
import { NxIconButton } from '../IconButton';
import { i18nInjectionKey } from '../internal';
import { NxModal } from '../Modal';
import NxFilePreview from './NxFilePreview.vue';

export interface NxPreviewFile {
  src: string;
  name: string;
  type?: string;
  size?: number | null;
  /** Video only: the picture shown before it plays (e.g. its thumbnail). */
  poster?: string;
}

const props = withDefaults(
  defineProps<{
    files: readonly NxPreviewFile[];
    downloadLabel?: string;
    previousLabel?: string;
    nextLabel?: string;
    /** Position text; `{n}` and `{total}` are replaced. */
    positionLabel?: string;
    locale?: string;
  }>(),
  {
    downloadLabel: 'Herunterladen',
    previousLabel: 'Vorherige Datei',
    nextLabel: 'Nächste Datei',
    positionLabel: '{n} von {total}',
    locale: undefined,
  }
);

const open = defineModel<boolean>('open', { required: true });
const index = defineModel<number>('index', { default: 0 });

const i18n = inject(i18nInjectionKey, null);
const locale = computed(() => props.locale ?? i18n?.locale?.value);

const total = computed(() => props.files.length);
const safeIndex = computed(() => Math.max(0, Math.min(total.value - 1, index.value)));
const file = computed(() => props.files[safeIndex.value]);
const subtitle = computed(() => {
  const parts: string[] = [];
  if (total.value > 1) {
    parts.push(
      props.positionLabel
        .replace('{n}', String(safeIndex.value + 1))
        .replace('{total}', String(total.value))
    );
  }
  if (typeof file.value?.size === 'number') parts.push(formatBytes(file.value.size, locale.value));
  return parts.join(' · ') || undefined;
});

function step(delta: number) {
  if (total.value < 2) return;
  index.value = (safeIndex.value + delta + total.value) % total.value;
}

function onKeydown(event: KeyboardEvent) {
  const target = event.target as HTMLElement;
  // Media controls and text selection keep their own arrow keys.
  if (target.closest('audio, video, input, textarea, [contenteditable="true"]')) return;
  if (event.key === 'ArrowLeft') {
    event.preventDefault();
    step(-1);
  } else if (event.key === 'ArrowRight') {
    event.preventDefault();
    step(1);
  }
}
</script>

<template>
  <NxModal
    v-model:open="open"
    :title="file?.name ?? ''"
    :subtitle="subtitle"
    size="wide"
    close-on-click-outside
  >
    <template #body>
      <div class="nx-file-preview-dialog__body" @keydown="onKeydown">
        <NxFilePreview
          v-if="file"
          :key="file.src"
          :src="file.src"
          :name="file.name"
          :type="file.type"
          :size="file.size"
          :poster="file.poster"
          :download-label="props.downloadLabel"
          :locale="locale"
        />
      </div>
    </template>
    <template #header-actions>
      <NxIconButton
        v-if="total > 1"
        height="s"
        :title="props.previousLabel"
        :aria-label="props.previousLabel"
        data-testid="nx-file-preview-prev"
        @click="step(-1)"
      >
        <ChevronLeft class="nxt-icon-md" aria-hidden="true" />
      </NxIconButton>
      <NxIconButton
        v-if="total > 1"
        height="s"
        :title="props.nextLabel"
        :aria-label="props.nextLabel"
        data-testid="nx-file-preview-next"
        @click="step(1)"
      >
        <ChevronRight class="nxt-icon-md" aria-hidden="true" />
      </NxIconButton>
      <a
        v-if="file"
        class="nx-file-preview-dialog__download"
        :href="file.src"
        :download="file.name"
        :title="props.downloadLabel"
        :aria-label="props.downloadLabel"
      >
        <Download class="nxt-icon-md" aria-hidden="true" />
      </a>
    </template>
  </NxModal>
</template>

<style scoped>
.nx-file-preview-dialog__body {
  display: flex;
  height: min(70vh, calc(var(--nxt-size-unit) * 44));
  min-height: var(--nxt-area-size-lg);
}
.nx-file-preview-dialog__download {
  display: inline-grid;
  place-items: center;
  width: var(--nxt-control-size-sm);
  height: var(--nxt-control-size-sm);
  border-radius: var(--nxt-radius-md);
  color: var(--nxt-fg-subtle);
}
.nx-file-preview-dialog__download:hover {
  background: var(--nxt-table-row-hover-bg);
  color: var(--nxt-fg);
}
.nx-file-preview-dialog__download:focus-visible {
  outline: none;
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
</style>
